import React, { useState } from 'react';
import styles from './VerificationPortal.module.css';

// Employee verification function - Updated for Certificate ID OR (Employee ID + Name)
const verifyEmployee = (certificateId, employeeId, name) => {
  const testEmployees = [
    {
      id: 'EXA-LTR-238705-40H2YZ',
      employeeId: '22006',
      name: 'Rajmani',
      dateOfJoining: '2021-07-05',
      dateOfRelieving: '2022-03-31',
      designation: 'Software Developer',
      department: 'Department of Technical Affairs',
      status: 'Verified'
    },
    {
      id: 'CERT-2024-002',
      employeeId: 'EMP-002',
      name: 'Priya Patel',
      dateOfJoining: '2021-03-10',
      dateOfRelieving: '2024-04-15',
      designation: 'Project Manager',
      department: 'Operations',
      status: 'Verified'
    },
    {
      id: 'CERT-2024-003',
      employeeId: 'EMP-003',
      name: 'Amit Kumar',
      dateOfJoining: '2023-01-20',
      dateOfRelieving: '2024-03-10',
      designation: 'UI/UX Designer',
      department: 'Design',
      status: 'Verified'
    },
    {
      id: 'CERT-2024-004',
      employeeId: 'EMP-004',
      name: 'Sneha Verma',
      dateOfJoining: '2020-08-05',
      dateOfRelieving: '2024-02-28',
      designation: 'HR Manager',
      department: 'Human Resources',
      status: 'Verified'
    },
    {
      id: 'CERT-2025-005',
      employeeId: 'EMP-005',
      name: 'Vikram Singh',
      dateOfJoining: '2022-11-15',
      dateOfRelieving: '2024-01-31',
      designation: 'Business Analyst',
      department: 'Strategy',
      status: 'Verified'
    }
  ];

  const certId = certificateId ? certificateId.trim().toUpperCase() : '';
  const empId = employeeId ? employeeId.trim().toUpperCase() : '';
  const empName = name ? name.trim().toLowerCase() : '';

  // CASE 1: Search by Certificate ID only (name optional for certificate ID search)
  if (certId) {
    const employee = testEmployees.find(emp => emp.id === certId);
    if (employee) {
      // If name is provided, verify it matches
      if (empName && employee.name.toLowerCase() !== empName) {
        return {
          success: false,
          message: 'Certificate ID found but name does not match. Please check the name and try again.'
        };
      }
      
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substr(2, 8);
      
      return {
        success: true,
        data: {
          certificateNumber: employee.id,
          name: employee.name,
          dateOfJoining: employee.dateOfJoining,
          dateOfRelieving: employee.dateOfRelieving,
          designation: employee.designation,
          department: employee.department,
          status: employee.status,
          verificationDate: new Date().toISOString(),
          verificationToken: `VER-${timestamp}-${randomId}`,
          systemId: `SYS-${timestamp}-${randomId}`
        }
      };
    } else {
      return {
        success: false,
        message: 'No certificate found with this ID. Please verify the Certificate ID and try again.'
      };
    }
  }
  
  // CASE 2: Search by Employee ID + Name (both required)
  if (empId && empName) {
    const employee = testEmployees.find(emp => emp.employeeId === empId);
    
    if (!employee) {
      return {
        success: false,
        message: 'No employee found with this Employee ID. Please verify the ID and try again.'
      };
    }
    
    if (employee.name.toLowerCase() !== empName) {
      return {
        success: false,
        message: 'Employee ID found but name does not match. Please check the name and try again.'
      };
    }
    
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substr(2, 8);
    
    return {
      success: true,
      data: {
        certificateNumber: employee.id,
        name: employee.name,
        dateOfJoining: employee.dateOfJoining,
        dateOfRelieving: employee.dateOfRelieving,
        designation: employee.designation,
        department: employee.department,
        status: employee.status,
        verificationDate: new Date().toISOString(),
        verificationToken: `VER-${timestamp}-${randomId}`,
        systemId: `SYS-${timestamp}-${randomId}`
      }
    };
  }

  // No valid search criteria
  return {
    success: false,
    message: 'Please provide either a Certificate ID, or both Employee ID and Name.'
  };
};

const VerificationPortal = () => {
  const [formData, setFormData] = useState({
    certificateId: '',
    employeeId: '',
    name: ''
  });
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeMethod, setActiveMethod] = useState('certificate');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (activeMethod === 'certificate') {
      if (!formData.certificateId.trim()) {
        newErrors.certificateId = 'Certificate ID is required';
      }
    } else {
      if (!formData.employeeId.trim()) {
        newErrors.employeeId = 'Employee ID is required';
      }
      if (!formData.name.trim()) {
        newErrors.name = 'Full name is required for employee ID verification';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setVerificationResult(null);
    
    setTimeout(() => {
      const result = verifyEmployee(
        formData.certificateId,
        formData.employeeId,
        formData.name
      );
      
      setVerificationResult(result);
      setLoading(false);
    }, 1000);
  };

  const resetForm = () => {
    setFormData({
      certificateId: '',
      employeeId: '',
      name: ''
    });
    setVerificationResult(null);
    setErrors({});
    setActiveMethod('certificate');
  };

  const generateCertificatePDF = () => {
    if (!verificationResult || !verificationResult.success) return;
    
    const data = verificationResult.data;
    const verificationDate = new Date(data.verificationDate);
    const printDate = verificationDate.toLocaleDateString('en-GB');
    const printTime = verificationDate.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
    
    const pdfContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Employment Verification Certificate - Exademy</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Georgia', 'Times New Roman', serif;
            background: #EDFFF7;
            color: #0F1E1B;
            line-height: 1.5;
            padding: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
        }
        
        .certificate {
            width: 210mm;
            min-height: 297mm;
            background: white;
            padding: 40px 50px;
            position: relative;
            box-shadow: 0 20px 40px rgba(11, 94, 66, 0.12);
            border: 1px solid rgba(11, 94, 66, 0.1);
            border-radius: 8px;
        }
        
        .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #0B5E42;
            position: relative;
        }
        
        .company-name {
            font-size: 32px;
            font-weight: 800;
            color: #0B5E42;
            margin-bottom: 5px;
            letter-spacing: 1px;
            font-family: 'Plus Jakarta Sans', sans-serif;
        }
        
        .title {
            font-size: 24px;
            font-weight: 700;
            color: #0B5E42;
            margin-bottom: 10px;
            text-transform: uppercase;
        }
        
        .subtitle {
            font-size: 16px;
            color: #475A56;
            margin-bottom: 5px;
        }
        
        .verified-stamp {
            position: absolute;
            top: 10px;
            right: 10px;
            width: 120px;
            height: 120px;
            border: 4px solid #0B5E42;
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background: rgba(237, 255, 247, 0.3);
            box-shadow: 0 0 20px rgba(11, 94, 66, 0.15);
            z-index: 10;
        }
        
        .stamp-text {
            font-size: 16px;
            font-weight: bold;
            color: #0B5E42;
            text-transform: uppercase;
        }
        
        .stamp-check {
            font-size: 32px;
            color: #0B5E42;
            margin: 5px 0;
        }
        
        .stamp-date {
            font-size: 12px;
            color: #475A56;
        }
        
        .content {
            margin: 30px 0;
        }
        
        .section {
            margin-bottom: 30px;
        }
        
        .section-title {
            font-size: 18px;
            font-weight: 700;
            color: #0B5E42;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid rgba(11, 94, 66, 0.2);
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        
        .info-table {
            width: 100%;
            border-collapse: collapse;
        }
        
        .info-row {
            margin-bottom: 15px;
            display: flex;
            align-items: flex-start;
        }
        
        .label {
            width: 200px;
            font-size: 14px;
            color: #475A56;
            font-weight: 600;
            padding: 8px 0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .value {
            flex: 1;
            font-size: 15px;
            color: #0F1E1B;
            padding: 8px 0;
            border-bottom: 1px dotted rgba(11, 94, 66, 0.2);
            margin-left: 20px;
            font-weight: 500;
        }
        
        .verified-badge {
            display: inline-block;
            background: #0B5E42;
            color: white;
            padding: 4px 15px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: bold;
            margin-left: 10px;
        }
        
        .footer {
            margin-top: 50px;
            padding-top: 25px;
            border-top: 2px solid #0B5E42;
            font-size: 12px;
            color: #475A56;
        }
        
        .footer-row {
            margin-bottom: 8px;
        }
        
        .footer-company {
            font-weight: bold;
            color: #0B5E42;
        }
        
        .footer-info {
            background: #EDFFF7;
            padding: 15px;
            margin: 15px 0;
            border-left: 4px solid #0B5E42;
            border-radius: 4px;
        }
        
        .warning-box {
            background: #FFF8E1;
            border: 1px solid #FFB300;
            padding: 15px;
            border-radius: 4px;
            margin-top: 20px;
        }
        
        .warning-title {
            font-weight: bold;
            color: #B33838;
            margin-bottom: 5px;
            font-size: 13px;
        }
        
        .print-section {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px dashed rgba(11, 94, 66, 0.3);
        }
        
        .print-btn {
            background: #0B5E42;
            color: white;
            border: none;
            padding: 12px 40px;
            border-radius: 30px;
            font-size: 14px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.2s;
        }
        
        .print-btn:hover {
            background: #084C35;
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(11, 94, 66, 0.2);
        }
        
        @media print {
            body {
                padding: 0;
                margin: 0;
                background: white;
            }
            
            .certificate {
                box-shadow: none;
                border: none;
                padding: 30mm;
            }
            
            .print-section {
                display: none;
            }
        }
        
        @page {
            size: A4;
            margin: 0;
        }
    </style>
</head>
<body>
    <div class="certificate">
        <div class="verified-stamp">
            <div class="stamp-text">VERIFIED</div>
            <div class="stamp-check">✓</div>
            <div class="stamp-date">${printDate}</div>
        </div>
        
        <div class="header">
            <div class="company-name">EXADEMY</div>
            <div class="title">Employment Verification Certificate</div>
            <div class="subtitle">Official Document - Digitally Verified</div>
        </div>
        
        <div class="content">
            <div class="section">
                <div class="section-title">Employee Information</div>
                <div class="info-table">
                    <div class="info-row">
                        <div class="label">Certificate Number</div>
                        <div class="value">${data.certificateNumber}</div>
                    </div>
                    <div class="info-row">
                        <div class="label">Full Name</div>
                        <div class="value">${data.name}</div>
                    </div>
                </div>
            </div>
            
            <div class="section">
                <div class="section-title">Employment Details</div>
                <div class="info-table">
                    <div class="info-row">
                        <div class="label">Date of Joining</div>
                        <div class="value">${data.dateOfJoining}</div>
                    </div>
                    <div class="info-row">
                        <div class="label">Date of Relieving</div>
                        <div class="value">${data.dateOfRelieving}</div>
                    </div>
                    <div class="info-row">
                        <div class="label">Designation</div>
                        <div class="value">${data.designation}</div>
                    </div>
                    <div class="info-row">
                        <div class="label">Department</div>
                        <div class="value">${data.department}</div>
                    </div>
                </div>
            </div>
            
            <div class="section">
                <div class="section-title">Verification Details</div>
                <div class="info-table">
                    <div class="info-row">
                        <div class="label">Verification Status</div>
                        <div class="value">
                            ${data.status}
                            <span class="verified-badge">VERIFIED</span>
                        </div>
                    </div>
                    <div class="info-row">
                        <div class="label">Verification Date & Time</div>
                        <div class="value">${verificationDate.toLocaleString()}</div>
                    </div>
                    <div class="info-row">
                        <div class="label">System ID</div>
                        <div class="value">${data.systemId}</div>
                    </div>
                    <div class="info-row">
                        <div class="label">Verification Token</div>
                        <div class="value">${data.verificationToken}</div>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="footer">
            <div class="footer-row footer-company">
                Exademy | Registered Office: Bhagalpur, Bihar
            </div>
            
            <div class="footer-row">
                Trademark Registration number: 4554507 | Email: info@exademy.in | Website: www.exademy.in
            </div>
            
            <div class="footer-info">
                <div class="footer-row">
                    <strong>Certificate Generated:</strong> ${printDate} at ${printTime}
                </div>
                <div class="footer-row">
                    <strong>System ID:</strong> ${data.systemId}
                </div>
                <div class="footer-row">
                    <strong>Verification URL:</strong> www.exademy.in/verify/${data.verificationToken}
                </div>
            </div>
            
            <div class="warning-box">
                <div class="warning-title">VERIFICATION NOTICE</div>
                <div class="footer-row">
                    This is a digitally verified document. Any unauthorized modification will invalidate this certificate.
                </div>
                <div class="footer-row">
                    To verify authenticity, visit www.exademy.in/verify and enter the verification token.
                </div>
            </div>
        </div>
        
        <div class="print-section">
            <button class="print-btn" onclick="window.print()">Print Certificate</button>
        </div>
    </div>
</body>
</html>
    `;
    
    const certificateWindow = window.open('', '_blank');
    certificateWindow.document.write(pdfContent);
    certificateWindow.document.close();
  };

  return (
    <div className={styles.verificationPortal}>
      {/* Header */}
      <div className={styles.verificationHeader}>
        <div className={styles.container}>
          <h1>Employee / Interns Verification Portal</h1>
          <p>Verify employment certificates issued by Exademy with our secure digital verification system</p>
        </div>
        <div className={styles.headerWave}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120">
            <path fill="#EDFFF7" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.verificationGrid}>
          {/* Verification Form */}
          <div className={styles.verificationFormContainer}>
            <div className={styles.formCard}>
              <div className={styles.formHeader}>
                <h2>Verify Certificate</h2>
                <p className={styles.formSubtitle}>
                  Choose your verification method
                </p>
              </div>
              
              {/* Method Toggle */}
              <div className={styles.methodToggle}>
                <button 
                  className={`${styles.methodBtn} ${activeMethod === 'certificate' ? styles.active : ''}`}
                  onClick={() => {
                    setActiveMethod('certificate');
                    setErrors({});
                  }}
                >
                  <i className="fas fa-certificate"></i>
                  By Certificate ID
                </button>
                <button 
                  className={`${styles.methodBtn} ${activeMethod === 'employee' ? styles.active : ''}`}
                  onClick={() => {
                    setActiveMethod('employee');
                    setErrors({});
                  }}
                >
                  <i className="fas fa-id-card"></i>
                  By Employee ID + Name
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                {/* Certificate ID Field - shown only for certificate method */}
                {activeMethod === 'certificate' && (
                  <div className={styles.formGroup}>
                    <label>
                      <i className="fas fa-certificate"></i>
                      Certificate ID
                    </label>
                    <input
                      type="text"
                      name="certificateId"
                      value={formData.certificateId}
                      onChange={handleInputChange}
                      placeholder="e.g., XXX-XXX-XXXXXX-XXXXXX"
                      className={`${styles.formInput} ${errors.certificateId ? styles.error : ''}`}
                      style={{ maxWidth: '500px' }}
                    />
                    {errors.certificateId && (
                      <span className={styles.errorMessage}>{errors.certificateId}</span>
                    )}
                    <span className={styles.inputHint}>
                      Name is optional for Certificate ID search
                    </span>
                  </div>
                )}

                {/* Employee ID Field - shown only for employee method */}
                {activeMethod === 'employee' && (
                  <div className={styles.formGroup}>
                    <label>
                      <i className="fas fa-id-badge"></i>
                      Employee ID
                    </label>
                    <input
                      type="text"
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      placeholder="e.g., XXXXX"
                      className={`${styles.formInput} ${errors.employeeId ? styles.error : ''}`}
                      style={{ maxWidth: '500px' }}
                    />
                    {errors.employeeId && (
                      <span className={styles.errorMessage}>{errors.employeeId}</span>
                    )}
                  </div>
                )}

                {/* Name Field - always shown but required only for employee method */}
                <div className={styles.formGroup}>
                  <label>
                    <i className="fas fa-user"></i>
                    Full Name {activeMethod === 'employee' && <span className={styles.requiredStar}>*</span>}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter employee's full name"
                    className={`${styles.formInput} ${errors.name ? styles.error : ''}`}
                    style={{ maxWidth: '500px' }}
                  />
                  {errors.name && (
                    <span className={styles.errorMessage}>{errors.name}</span>
                  )}
                  {activeMethod === 'certificate' && (
                    <span className={styles.inputHint}>
                      Optional - helps verify name match
                    </span>
                  )}
                </div>
                
                <button 
                  type="submit" 
                  className={styles.verifyBtn}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className={styles.spinner}></span>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-shield-alt"></i>
                      Verify Certificate
                    </>
                  )}
                </button>
              </form>
              
              <div className={styles.verificationNote}>
                <div className={styles.noteIcon}>🔒</div>
                <div className={styles.noteContent}>
                  <strong>Secure Verification</strong>
                  <p>All verifications are encrypted, timestamped, and logged for audit purposes.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className={styles.resultsContainer}>
            {verificationResult ? (
              verificationResult.success ? (
                <div className={`${styles.resultCard} ${styles.success}`}>
                  <div className={styles.resultHeader}>
                    <div className={styles.statusIndicator}>
                      <i className="fas fa-check-circle"></i>
                      Verified Successfully
                    </div>
                    <button onClick={resetForm} className={styles.resetBtn}>
                      <i className="fas fa-plus-circle"></i>
                      New Verification
                    </button>
                  </div>
                  
                  <div className={styles.verificationDetails}>
                    <div className={styles.verifiedBadgeLarge}>
                      <div className={styles.verifiedIcon}>✓</div>
                      <span>DIGITALLY VERIFIED</span>
                    </div>
                    
                    <h3>Employee Verification Details</h3>
                    
                    <div className={styles.detailsGrid}>
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Certificate Number</span>
                        <span className={styles.detailValue}>{verificationResult.data.certificateNumber}</span>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Full Name</span>
                        <span className={styles.detailValue}>{verificationResult.data.name}</span>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Date of Joining</span>
                        <span className={styles.detailValue}>{verificationResult.data.dateOfJoining}</span>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Date of Relieving</span>
                        <span className={styles.detailValue}>{verificationResult.data.dateOfRelieving}</span>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Designation</span>
                        <span className={styles.detailValue}>{verificationResult.data.designation}</span>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Department</span>
                        <span className={styles.detailValue}>{verificationResult.data.department}</span>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Verification Status</span>
                        <span className={`${styles.detailValue} ${styles.statusBadge}`}>
                          {verificationResult.data.status}
                        </span>
                      </div>
                      
                      <div className={styles.detailItem}>
                        <span className={styles.detailLabel}>Verification Date</span>
                        <span className={styles.detailValue}>
                          {new Date(verificationResult.data.verificationDate).toLocaleString()}
                        </span>
                      </div>
                      
                      <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                        <span className={styles.detailLabel}>Verification Token</span>
                        <span className={styles.detailValueToken}>{verificationResult.data.verificationToken}</span>
                      </div>
                      
                      <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                        <span className={styles.detailLabel}>System ID</span>
                        <span className={styles.detailValue}>{verificationResult.data.systemId}</span>
                      </div>
                    </div>
                    
                    <div className={styles.verificationNoteSection}>
                      <i className="fas fa-info-circle"></i>
                      <p>
                        This certificate has been digitally verified and timestamped. 
                        For More Clarification , Please Contact our HR Team at hr@exademy.in
                      </p>
                    </div>
                    
                    <div className={styles.downloadSection}>
                      <button onClick={generateCertificatePDF} className={styles.downloadBtn}>
                        <i className="fas fa-file-pdf"></i>
                        Generate Verified Certificate (PDF)
                      </button>
                      <p className={styles.downloadNote}>
                        Opens a professionally formatted, printable certificate with security features
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`${styles.resultCard} ${styles.error}`}>
                  <div className={styles.resultHeader}>
                    <div className={styles.statusIndicatorError}>
                      <i className="fas fa-exclamation-circle"></i>
                      Verification Failed
                    </div>
                    <button onClick={resetForm} className={styles.resetBtn}>
                      <i className="fas fa-redo"></i>
                      Try Again
                    </button>
                  </div>
                  <div className={styles.errorContent}>
                    <div className={styles.errorIcon}>!</div>
                    <p className={styles.errorMessage}>{verificationResult.message}</p>
                    <div className={styles.errorHelp}>
                      <h4>Need help?</h4>
                      <ul>
                        <li>✓ Check if you're using the correct verification method</li>
                        <li>✓ Certificate ID is case-sensitive</li>
                        <li>✓ Employee ID search requires both ID and name</li>
                        <li>✓ Contact HR at hr@exademy.in</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className={`${styles.resultCard} ${styles.empty}`}>
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <h3>Ready to Verify</h3>
                  <p>Enter employee / intern details to verify their certificate</p>
                  <div className={styles.verificationSteps}>
                    <div className={styles.step}>
                      <span className={styles.stepNumber}>1</span>
                      <div className={styles.stepContent}>
                        <strong>Choose method</strong>
                        <span>Certificate ID OR Employee ID + Name</span>
                      </div>
                    </div>
                    <div className={styles.step}>
                      <span className={styles.stepNumber}>2</span>
                      <div className={styles.stepContent}>
                        <strong>Enter details</strong>
                        <span>Provide the required information</span>
                      </div>
                    </div>
                    <div className={styles.step}>
                      <span className={styles.stepNumber}>3</span>
                      <div className={styles.stepContent}>
                        <strong>Verify & Download</strong>
                        <span>Get instant verification result</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationPortal;